import type { Widget, WidgetType } from "../schema/widgets";
import { semantic } from "../theme/tokens";
import { Agreements } from "./Agreements";
import { BlogPosts } from "./BlogPosts";
import { CalendlyEmbed } from "./CalendlyEmbed";
import { ContentCard } from "./ContentCard";
import { CustomerLogos } from "./CustomerLogos";
import { CustomerTestimonials } from "./CustomerTestimonials";
import { DocuSignEmbed } from "./DocuSignEmbed";
import { FAQ } from "./FAQ";
import { FeatureRows } from "./FeatureRows";
import { Gantt } from "./Gantt";
import { GDocEmbed } from "./GDocEmbed";
import { Image } from "./Image";
import { LoomEmbed } from "./LoomEmbed";
import { MAP } from "./MAP";
import { MarketplaceGrid } from "./MarketplaceGrid";
import { NetworkMap } from "./NetworkMap";
import { NextStep } from "./NextStep";
import { PDFEmbed } from "./PDFEmbed";
import { PDFGrid } from "./PDFGrid";
import { ProgramAccordion } from "./ProgramAccordion";
import { ProgramsExplorer } from "./ProgramsExplorer";
import { RichText } from "./RichText";
import { Scene } from "./Scene";
import { StatBand } from "./StatBand";
import { TabbedTable } from "./TabbedTable";
import { TeamGrid } from "./TeamGrid";
import { WistiaEmbed } from "./WistiaEmbed";
import { YouTubeEmbed } from "./YouTubeEmbed";
import { BarChartWidget } from "./live/BarChartWidget";
import { DonutChartWidget } from "./live/DonutChartWidget";
import { Callout } from "./live/Callout";
import { Responsibilities } from "./Responsibilities";
import { MetricDefinitions } from "./MetricDefinitions";
import { ChannelCoverage } from "./live/ChannelCoverage";
import { CompetitorBenchmark } from "./live/CompetitorBenchmark";
import { LeverCards } from "./live/LeverCards";
import { MetricTiles } from "./live/MetricTiles";
import { OpportunitySnapshot } from "./live/OpportunitySnapshot";
import { PricingCompetitiveness } from "./live/PricingCompetitiveness";
import { SuppressionDiagnoses } from "./live/SuppressionDiagnoses";
import type { WidgetComponent, WidgetDispatchProps } from "./types";

// Widget registry — single dispatch point from JSON `type` to React component.
// Add new widgets here. Compile error if a WidgetType is missing.
//
// Why a Record<WidgetType, ...>: TypeScript enforces exhaustiveness so adding
// a widget to the schema without registering it here fails the build.
const registry: Record<WidgetType, WidgetComponent> = {
    rich_text: RichText as WidgetComponent,
    content_card: ContentCard as WidgetComponent,
    loom_embed: LoomEmbed as WidgetComponent,
    calendly_embed: CalendlyEmbed as WidgetComponent,
    pdf_embed: PDFEmbed as WidgetComponent,
    pdf_grid: PDFGrid as WidgetComponent,
    next_step: NextStep as WidgetComponent,
    youtube_embed: YouTubeEmbed as WidgetComponent,
    wistia_embed: WistiaEmbed as WidgetComponent,
    gdoc_embed: GDocEmbed as WidgetComponent,
    docusign_embed: DocuSignEmbed as WidgetComponent,
    agreements: Agreements as WidgetComponent,
    marketplace_grid: MarketplaceGrid as WidgetComponent,
    feature_rows: FeatureRows as WidgetComponent,
    scene: Scene as WidgetComponent,
    stat_band: StatBand as WidgetComponent,
    gantt: Gantt as WidgetComponent,
    team_grid: TeamGrid as WidgetComponent,
    customer_logos: CustomerLogos as WidgetComponent,
    customer_testimonials: CustomerTestimonials as WidgetComponent,
    blog_posts: BlogPosts as WidgetComponent,
    faq: FAQ as WidgetComponent,
    map: MAP as WidgetComponent,
    image: Image as WidgetComponent,
    network_map: NetworkMap as WidgetComponent,
    tabbed_table: TabbedTable as WidgetComponent,
    program_accordion: ProgramAccordion as WidgetComponent,
    programs_explorer: ProgramsExplorer as WidgetComponent,

    opportunity_snapshot: OpportunitySnapshot as WidgetComponent,
    suppression_diagnoses: SuppressionDiagnoses as WidgetComponent,
    channel_coverage: ChannelCoverage as WidgetComponent,
    pricing_competitiveness: PricingCompetitiveness as WidgetComponent,
    metric_tiles: MetricTiles as WidgetComponent,
    competitor_benchmark: CompetitorBenchmark as WidgetComponent,
    bar_chart: BarChartWidget as WidgetComponent,
    donut_chart: DonutChartWidget as WidgetComponent,
    lever_cards: LeverCards as WidgetComponent,
    callout: Callout as WidgetComponent,
    responsibilities: Responsibilities as WidgetComponent,
    metric_definitions: MetricDefinitions as WidgetComponent,
};

// Public dispatcher used by page templates. Accepts any Widget;
// the discriminated union narrows by `type` inside the registry lookup.
export function WidgetRenderer({ widget, room }: WidgetDispatchProps) {
    const Component = registry[widget.type];
    if (!Component) {
        return <UnknownWidget widget={widget} />;
    }
    return <Component widget={widget as never} room={room} />;
}

// ─── Helpers ─────────────────────────────────────────────────────────

function UnknownWidget({ widget }: { widget: Widget }) {
    return (
        <div
            style={{
                padding: 16,
                border: `1px solid ${semantic.danger.border}`,
                borderRadius: 8,
                color: semantic.danger.text,
                background: semantic.danger.bg,
            }}
        >
            Unknown widget type: {(widget as { type: string }).type}
        </div>
    );
}
